import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { UserRound, FileUp, Loader2, ShieldCheck, Plane, HeartPulse, CreditCard, ScrollText, RefreshCw, CheckCircle2, XCircle, ChevronsUpDown, Check } from "lucide-react";

const AEROLINEAS_TOP = ["Aeromexico", "Viva Aerobus", "Volaris", "Delta", "American Airlines"];
const _norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

const AirlineCombobox = ({ value, airlines, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const names = [...new Set(airlines.map((a) => a.name).filter(Boolean))];
  const top = AEROLINEAS_TOP.map((t) => names.find((n) => _norm(n) === _norm(t)) || t);
  const rest = names.filter((n) => !top.some((t) => _norm(t) === _norm(n))).sort();
  const ordered = [...top, ...rest];
  const filtered = query ? ordered.filter((n) => _norm(n).includes(_norm(query))) : ordered;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="combobox-aerolinea">
          <span className={value ? "" : "text-slate-400"}>{value || "Escribe o selecciona…"}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Escribe para filtrar…" value={query} onValueChange={setQuery} data-testid="combobox-aerolinea-input" />
          <CommandList className="max-h-56">
            <CommandEmpty className="p-2">
              {query ? (
                <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => { onChange(query); setOpen(false); }} data-testid="combobox-aerolinea-custom">
                  Usar "{query}"
                </Button>
              ) : "Sin resultados"}
            </CommandEmpty>
            <CommandGroup>
              {filtered.map((n) => (
                <CommandItem key={n} value={n} onSelect={() => { onChange(n); setOpen(false); }} data-testid={`aerolinea-opt-${_norm(n).replace(/\s+/g, "-")}`}>
                  <Check className={`w-4 h-4 mr-2 ${value === n ? "opacity-100" : "opacity-0"}`} />
                  {n}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const DEPARTAMENTOS = ["Dirección", "Comercial", "Marketing", "Hospitalidad", "Traveldesk", "Administración & Finanzas", "Logística"];
const DOC_LABELS = { id: "Identificación oficial (INE/Pasaporte)", pasaporte: "Pasaporte vigente", visa: "Visa americana/canadiense" };

const EstatusBadge = ({ estatus }) => {
  const map = {
    vigente: { label: "Vigente", cls: "bg-emerald-100 text-emerald-700" },
    por_vencer: { label: "Por vencer", cls: "bg-amber-100 text-amber-700" },
    vencido: { label: "Vencido", cls: "bg-red-100 text-red-700" },
    sin_fecha: { label: "Sin fecha", cls: "bg-slate-100 text-slate-600" },
  };
  const m = map[estatus] || map.sin_fecha;
  return <Badge className={`${m.cls} border-0`} data-testid={`doc-estatus-${estatus}`}>{m.label}</Badge>;
};

const DocUploader = ({ tipo, doc, onUploaded, required }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", tipo);
      const res = await axios.post(`${API}/viajeros/documentos/upload`, fd, { withCredentials: true });
      onUploaded(res.data);
      if (res.data?.analysis?.es_legible) {
        toast.success(`Documento analizado: vence ${res.data.fecha_vencimiento || "fecha no detectada"}`);
      } else {
        toast.warning("Documento subido, pero no se pudieron leer las fechas. Captúralas manualmente.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir documento");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-2" data-testid={`doc-uploader-${tipo}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-700">
          📎 {DOC_LABELS[tipo]} {required && <span className="text-red-500">*</span>}
        </p>
        {doc && <EstatusBadge estatus={doc.estatus} />}
      </div>
      {doc ? (
        <div className="text-xs text-slate-600 space-y-1">
          <p className="truncate">📄 {doc.filename}</p>
          <p>Expedición: <b>{doc.fecha_expedicion || "—"}</b> · Vencimiento: <b>{doc.fecha_vencimiento || "—"}</b></p>
        </div>
      ) : (
        <p className="text-xs text-slate-400">Sin documento cargado</p>
      )}
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={handleFile} data-testid={`doc-file-input-${tipo}`} />
      <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`doc-upload-btn-${tipo}`}>
        {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando documento…</>) : (<><FileUp className="w-4 h-4 mr-2" /> {doc ? "Reemplazar documento" : "Subir documento"}</>)}
      </Button>
    </div>
  );
};

export default function MiPerfilViajero() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [airlines, setAirlines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // Renovación
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewTipo, setRenewTipo] = useState("pasaporte");
  const [renewing, setRenewing] = useState(false);
  const [renewResult, setRenewResult] = useState(null);
  const renewFileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [pRes, aRes] = await Promise.all([
        axios.get(`${API}/viajeros/me`, { withCredentials: true }),
        axios.get(`${API}/airlines`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setProfile(pRes.data);
      setAirlines(aRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar tu perfil");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const renovar = searchParams.get("renovar");
    if (renovar) {
      setRenewTipo(["id", "pasaporte", "visa"].includes(renovar) ? renovar : "pasaporte");
      setRenewOpen(true);
    }
  }, [searchParams]);

  const f = (key) => profile?.[key] ?? "";
  const set = (key, val) => setProfile((p) => ({ ...p, [key]: val }));
  const docByTipo = (tipo) => (profile?.documentos || []).find((d) => d.tipo === tipo);

  const onDocUploaded = (doc) => {
    setProfile((p) => {
      const docs = (p.documentos || []).filter((d) => d.tipo !== doc.tipo);
      const next = { ...p, documentos: [...docs, doc] };
      if (doc.tipo === "pasaporte") {
        if (doc.fecha_vencimiento) next.pasaporte_vencimiento = doc.fecha_vencimiento;
        if (doc.numero_documento) next.pasaporte_numero = doc.numero_documento;
      }
      if (doc.tipo === "id" && doc.fecha_vencimiento) next.id_vencimiento = doc.fecha_vencimiento;
      if (doc.tipo === "visa" && doc.fecha_vencimiento) next.visa_vencimiento = doc.fecha_vencimiento;
      return next;
    });
  };

  const save = async () => {
    if (!profile.nombre_completo?.trim()) return toast.error("El nombre completo es obligatorio");
    if (!profile.consentimiento_aceptado) return toast.error("Debes aceptar el aviso de privacidad");
    if (!docByTipo("id")) return toast.error("Debes subir tu identificación oficial");
    if (!docByTipo("pasaporte")) return toast.error("Debes subir tu pasaporte vigente");
    setSaving(true);
    try {
      const res = await axios.put(`${API}/viajeros/me`, profile, { withCredentials: true });
      setProfile(res.data);
      toast.success("Perfil de viajero guardado correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el perfil");
    } finally {
      setSaving(false);
    }
  };

  const submitRenewal = async () => {
    const file = renewFileRef.current?.files?.[0];
    if (!file) return toast.error("Selecciona el documento a renovar");
    setRenewing(true);
    setRenewResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tipo", renewTipo);
      const res = await axios.post(`${API}/viajeros/renovacion`, fd, { withCredentials: true });
      setRenewResult(res.data);
      if (res.data.valido) {
        toast.success("Documento renovado correctamente");
        onDocUploaded(res.data.documento);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar la renovación");
    } finally {
      setRenewing(false);
    }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Cargando perfil…</div>;
  if (!profile) return <div className="p-8 text-slate-500">No se pudo cargar tu perfil.</div>;

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6" data-testid="mi-perfil-viajero-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><UserRound className="w-6 h-6 text-cyan-700" /> Mi Perfil de Viaje</h1>
          <p className="text-sm text-slate-500 mt-1">Registra tus datos, documentos y preferencias de viaje.</p>
        </div>
        <Button variant="outline" onClick={() => { setRenewResult(null); setRenewOpen(true); }} data-testid="open-renovacion-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Renovar documento
        </Button>
      </div>

      {/* Datos personales */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><UserRound className="w-4 h-4 text-cyan-700" /> Datos personales</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5 md:col-span-2">
            <Label>Nombre completo (tal como aparece en el pasaporte) *</Label>
            <Input value={f("nombre_completo")} onChange={(e) => set("nombre_completo", e.target.value.toUpperCase())} data-testid="input-nombre-completo" />
          </div>
          <div className="space-y-1.5">
            <Label>Fecha de nacimiento</Label>
            <Input type="date" value={f("fecha_nacimiento")} onChange={(e) => set("fecha_nacimiento", e.target.value)} data-testid="input-fecha-nacimiento" />
          </div>
          <div className="space-y-1.5">
            <Label>Nacionalidad</Label>
            <Input value={f("nacionalidad")} onChange={(e) => set("nacionalidad", e.target.value.toUpperCase())} data-testid="input-nacionalidad" />
          </div>
          <div className="space-y-1.5">
            <Label>Área / Departamento</Label>
            <Select value={f("departamento") || undefined} onValueChange={(v) => set("departamento", v)}>
              <SelectTrigger data-testid="select-departamento"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent>{DEPARTAMENTOS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Correo corporativo</Label>
            <Input value={profile.email} disabled data-testid="input-email-corporativo" />
          </div>
          <div className="space-y-1.5">
            <Label>Teléfono celular</Label>
            <Input value={f("telefono")} onChange={(e) => set("telefono", e.target.value)} data-testid="input-telefono" />
          </div>
        </CardContent>
      </Card>

      {/* Documentos */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cyan-700" /> Documentos obligatorios</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DocUploader tipo="id" doc={docByTipo("id")} onUploaded={onDocUploaded} required />
            <DocUploader tipo="pasaporte" doc={docByTipo("pasaporte")} onUploaded={onDocUploaded} required />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Número de pasaporte</Label>
              <Input value={f("pasaporte_numero")} onChange={(e) => set("pasaporte_numero", e.target.value)} data-testid="input-pasaporte-numero" />
            </div>
            <div className="space-y-1.5">
              <Label>Vencimiento de pasaporte</Label>
              <Input type="date" value={f("pasaporte_vencimiento")} onChange={(e) => set("pasaporte_vencimiento", e.target.value)} data-testid="input-pasaporte-vencimiento" />
            </div>
            <div className="space-y-1.5">
              <Label>Vencimiento de identificación</Label>
              <Input type="date" value={f("id_vencimiento")} onChange={(e) => set("id_vencimiento", e.target.value)} data-testid="input-id-vencimiento" />
            </div>
          </div>
          <div className="flex items-center gap-3 pt-2">
            <Switch checked={!!profile.visa_tiene} onCheckedChange={(v) => set("visa_tiene", v)} data-testid="switch-visa" />
            <Label>¿Cuentas con visa americana/canadiense vigente?</Label>
          </div>
          {profile.visa_tiene && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <DocUploader tipo="visa" doc={docByTipo("visa")} onUploaded={onDocUploaded} />
              <div className="space-y-1.5">
                <Label>Tipo de visa</Label>
                <Select value={f("visa_tipo") || undefined} onValueChange={(v) => set("visa_tipo", v)}>
                  <SelectTrigger data-testid="select-visa-tipo"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="americana">Americana</SelectItem>
                    <SelectItem value="canadiense">Canadiense</SelectItem>
                    <SelectItem value="ambas">Ambas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Vencimiento de visa</Label>
                <Input type="date" value={f("visa_vencimiento")} onChange={(e) => set("visa_vencimiento", e.target.value)} data-testid="input-visa-vencimiento" />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Número de viajero confiable (Global Entry, TSA PreCheck, Viajero Confiable) — si aplica</Label>
            <Input value={f("viajero_confiable_numero")} onChange={(e) => set("viajero_confiable_numero", e.target.value)} data-testid="input-viajero-confiable" />
          </div>
        </CardContent>
      </Card>

      {/* Preferencias */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Plane className="w-4 h-4 text-cyan-700" /> Preferencias de viaje</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Aerolínea de preferencia</Label>
            <AirlineCombobox value={f("aerolinea_preferida")} airlines={airlines} onChange={(v) => set("aerolinea_preferida", v)} />
          </div>
          <div className="space-y-1.5">
            <Label>Programas de lealtad (viajero frecuente / millas)</Label>
            <Input value={f("programas_lealtad")} onChange={(e) => set("programas_lealtad", e.target.value)} placeholder="Ej. AM 12345678, AAdvantage 987…" data-testid="input-programas-lealtad" />
          </div>
          <div className="space-y-1.5">
            <Label>Preferencia de asiento</Label>
            <Select value={f("asiento_preferencia") || undefined} onValueChange={(v) => set("asiento_preferencia", v)}>
              <SelectTrigger data-testid="select-asiento"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ventana">Ventana</SelectItem>
                <SelectItem value="pasillo">Pasillo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Cadena hotelera de preferencia</Label>
            <Input value={f("cadena_hotelera")} onChange={(e) => set("cadena_hotelera", e.target.value)} placeholder="Ej. Marriott, Hilton, Hyatt…" data-testid="input-cadena-hotelera" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Preferencias alimentarias o restricciones (vegetariano, kosher, alergias…)</Label>
            <Textarea rows={2} value={f("preferencias_alimentarias")} onChange={(e) => set("preferencias_alimentarias", e.target.value)} data-testid="input-preferencias-alimentarias" />
          </div>
        </CardContent>
      </Card>

      {/* Salud y emergencias */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><HeartPulse className="w-4 h-4 text-cyan-700" /> Salud y emergencias</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label>Contacto de emergencia — Nombre</Label>
            <Input value={f("emergencia_nombre")} onChange={(e) => set("emergencia_nombre", e.target.value)} data-testid="input-emergencia-nombre" />
          </div>
          <div className="space-y-1.5">
            <Label>Parentesco</Label>
            <Input value={f("emergencia_parentesco")} onChange={(e) => set("emergencia_parentesco", e.target.value)} data-testid="input-emergencia-parentesco" />
          </div>
          <div className="space-y-1.5">
            <Label>Teléfono</Label>
            <Input value={f("emergencia_telefono")} onChange={(e) => set("emergencia_telefono", e.target.value)} data-testid="input-emergencia-telefono" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>Condiciones médicas relevantes o medicamentos (opcional / confidencial)</Label>
            <Textarea rows={2} value={f("condiciones_medicas")} onChange={(e) => set("condiciones_medicas", e.target.value)} data-testid="input-condiciones-medicas" />
          </div>
          <div className="space-y-1.5">
            <Label>Tipo de sangre (opcional)</Label>
            <Select value={f("tipo_sangre") || undefined} onValueChange={(v) => set("tipo_sangre", v)}>
              <SelectTrigger data-testid="select-tipo-sangre"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent>{["O+","O-","A+","A-","B+","B-","AB+","AB-"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-3">
            <Label>Alergias importantes</Label>
            <Textarea rows={2} value={f("alergias")} onChange={(e) => set("alergias", e.target.value)} data-testid="input-alergias" />
          </div>
        </CardContent>
      </Card>

      {/* Extras */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><CreditCard className="w-4 h-4 text-cyan-700" /> Datos adicionales</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Tarjetas corporativas asignadas</Label>
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => set("tarjetas_corporativas", [...(profile.tarjetas_corporativas || []), { tipo: "", ult4: "" }])}
                data-testid="add-tarjeta-btn"
              >
                + Agregar tarjeta
              </Button>
            </div>
            {(profile.tarjetas_corporativas || []).length === 0 && (
              <p className="text-xs text-slate-400">Sin tarjetas asignadas. Usa "Agregar tarjeta" si cuentas con una.</p>
            )}
            {(profile.tarjetas_corporativas || []).map((tc, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Select
                  value={tc.tipo || undefined}
                  onValueChange={(v) => set("tarjetas_corporativas", profile.tarjetas_corporativas.map((t, idx) => idx === i ? { ...t, tipo: v } : t))}
                >
                  <SelectTrigger className="flex-1" data-testid={`select-tarjeta-tipo-${i}`}><SelectValue placeholder="Tipo de tarjeta…" /></SelectTrigger>
                  <SelectContent>
                    {["AMEX ORO", "AMEX PLATA", "INVEX VOLARIS", "BBVA PYME"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  className="w-40" maxLength={4} placeholder="Últimos 4"
                  value={tc.ult4 || ""}
                  onChange={(e) => set("tarjetas_corporativas", profile.tarjetas_corporativas.map((t, idx) => idx === i ? { ...t, ult4: e.target.value.replace(/\D/g, "") } : t))}
                  data-testid={`input-tarjeta-ult4-${i}`}
                />
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={() => set("tarjetas_corporativas", profile.tarjetas_corporativas.filter((_, idx) => idx !== i))}
                  data-testid={`remove-tarjeta-${i}`}
                >
                  <XCircle className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <Label>Talla de uniforme / playera</Label>
            <Select value={f("talla") || undefined} onValueChange={(v) => set("talla", v)}>
              <SelectTrigger data-testid="select-talla"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent>{["XS","S","M","L","XL","XXL"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Consentimiento */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ScrollText className="w-4 h-4 text-cyan-700" /> Consentimiento</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500 leading-relaxed">
            En cumplimiento con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP),
            PD Travel tratará tus datos personales, incluidos documentos oficiales y datos sensibles de salud, exclusivamente
            para la gestión de tus viajes corporativos, emisión de seguros y trámites migratorios. Tus datos no serán
            compartidos con terceros salvo proveedores necesarios para la prestación del servicio.
          </p>
          <div className="flex items-start gap-3">
            <Checkbox checked={!!profile.consentimiento_aceptado} onCheckedChange={(v) => set("consentimiento_aceptado", !!v)} data-testid="checkbox-consentimiento" />
            <Label className="text-sm leading-snug cursor-pointer" onClick={() => set("consentimiento_aceptado", !profile.consentimiento_aceptado)}>
              He leído el aviso de privacidad y otorgo mi consentimiento para el tratamiento de mis datos personales. *
            </Label>
          </div>
          {profile.consentimiento_fecha && <p className="text-xs text-slate-400">Aceptado el {new Date(profile.consentimiento_fecha).toLocaleString("es-MX")}</p>}
        </CardContent>
      </Card>

      <div className="flex justify-end pb-8">
        <Button onClick={save} disabled={saving} className="bg-cyan-700 hover:bg-cyan-800" data-testid="save-perfil-btn">
          {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</>) : "Guardar perfil"}
        </Button>
      </div>

      {/* Diálogo de renovación */}
      <Dialog open={renewOpen} onOpenChange={(o) => { setRenewOpen(o); if (!o) { setRenewResult(null); setSearchParams({}); } }}>
        <DialogContent data-testid="renovacion-dialog">
          <DialogHeader><DialogTitle>Renovación de documento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Tipo de documento a renovar</Label>
              <Select value={renewTipo} onValueChange={setRenewTipo}>
                <SelectTrigger data-testid="select-renovacion-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="id">Identificación oficial (INE)</SelectItem>
                  <SelectItem value="pasaporte">Pasaporte</SelectItem>
                  <SelectItem value="visa">Visa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Documento renovado (JPG, PNG, WEBP o PDF)</Label>
              <input ref={renewFileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="block w-full text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-cyan-50 file:text-cyan-700" data-testid="renovacion-file-input" />
            </div>
            {renewResult && (
              <div className={`rounded-lg p-3 text-sm flex items-start gap-2 ${renewResult.valido ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid="renovacion-resultado">
                {renewResult.valido ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> : <XCircle className="w-5 h-5 flex-shrink-0" />}
                <div>
                  {renewResult.valido ? (
                    <p>Documento validado y actualizado. Nueva vigencia: <b>{renewResult.documento?.fecha_vencimiento || "—"}</b></p>
                  ) : (
                    <p>{renewResult.motivo}</p>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRenewOpen(false); setRenewResult(null); setSearchParams({}); }}>Cerrar</Button>
            <Button onClick={submitRenewal} disabled={renewing} className="bg-cyan-700 hover:bg-cyan-800" data-testid="renovacion-submit-btn">
              {renewing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Validando con IA…</>) : "Validar y renovar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
